import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import catalogService from '@/services/catalogService.ts';
import type { CatalogEntry, CatalogKind } from '@/types/catalog.ts';

const STALE_MS = 10 * 60 * 1000;

interface KindState {
  data?: CatalogEntry[];
  loading: boolean;
  error?: string;
  fetchedAt?: number;
  inflight?: Promise<CatalogEntry[]>;
}

const EMPTY_STATE: KindState = { loading: false };

const stateMap = new Map<CatalogKind, KindState>();
const listeners = new Map<CatalogKind, Set<() => void>>();

function getState(kind: CatalogKind): KindState {
  return stateMap.get(kind) ?? EMPTY_STATE;
}

function setState(kind: CatalogKind, next: KindState): void {
  stateMap.set(kind, next);
  listeners.get(kind)?.forEach((fn) => fn());
}

function subscribe(kind: CatalogKind, fn: () => void): () => void {
  let set = listeners.get(kind);
  if (!set) {
    set = new Set();
    listeners.set(kind, set);
  }
  set.add(fn);
  return () => {
    set!.delete(fn);
  };
}

async function fetchKind(kind: CatalogKind, force = false): Promise<CatalogEntry[]> {
  const cur = getState(kind);
  const fresh = !!cur.fetchedAt && Date.now() - cur.fetchedAt < STALE_MS;
  if (!force && cur.data && fresh) return cur.data;
  if (!force && cur.inflight) return cur.inflight;

  const promise = catalogService.getByKind(kind);
  setState(kind, { ...cur, loading: true, error: undefined, inflight: promise });

  try {
    const data = await promise;
    setState(kind, { data, loading: false, fetchedAt: Date.now() });
    return data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'CATALOG_FETCH_ERROR';
    setState(kind, { ...cur, loading: false, error: message, inflight: undefined });
    throw err;
  }
}

export function invalidateCatalog(kind?: CatalogKind): void {
  if (kind) {
    stateMap.delete(kind);
    listeners.get(kind)?.forEach((fn) => fn());
    return;
  }
  const keys = Array.from(stateMap.keys());
  stateMap.clear();
  for (const k of keys) listeners.get(k)?.forEach((fn) => fn());
}

interface UseCatalogResult {
  data: CatalogEntry[] | undefined;
  loading: boolean;
  error: string | undefined;
  refetch: () => Promise<void>;
}

export function useCatalog(kind: CatalogKind): UseCatalogResult {
  const sub = useCallback((fn: () => void) => subscribe(kind, fn), [kind]);
  const snapshot = useCallback(() => getState(kind), [kind]);
  const state = useSyncExternalStore(sub, snapshot, snapshot);

  useEffect(() => {
    fetchKind(kind).catch(() => {});
  }, [kind]);

  const refetch = useCallback(async () => {
    await fetchKind(kind, true).catch(() => {});
  }, [kind]);

  return { data: state.data, loading: state.loading, error: state.error, refetch };
}

function pickLabel(entry: CatalogEntry, lang: string): string {
  return entry.labels[lang] ?? entry.labels.es ?? entry.slug;
}

function pickBriefing(entry: CatalogEntry, lang: string): string {
  return entry.briefings[lang] ?? entry.briefings.es ?? '';
}

export interface CatalogOption {
  value: string;
  label: string;
  parentSlug?: string | null;
  sortOrder: number;
}

export function useCatalogOptions(kind: CatalogKind): {
  options: CatalogOption[];
  loading: boolean;
  error: string | undefined;
} {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language ?? 'es';
  const { data, loading, error } = useCatalog(kind);

  const options = useMemo<CatalogOption[]>(() => {
    if (!data) return [];
    return [...data]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((entry) => ({
        value: entry.slug,
        label: pickLabel(entry, lang),
        parentSlug: entry.parentSlug,
        sortOrder: entry.sortOrder,
      }));
  }, [data, lang]);

  return { options, loading, error };
}

export function useCatalogLabel(kind: CatalogKind, slug: string | undefined | null): string {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language ?? 'es';
  const { data } = useCatalog(kind);
  if (!slug) return '';
  if (!data) return slug;
  const entry = data.find((e) => e.slug === slug);
  return entry ? pickLabel(entry, lang) : slug;
}

export function useCatalogBriefing(kind: CatalogKind, slug: string | undefined | null): string {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language ?? 'es';
  const { data } = useCatalog(kind);
  if (!slug || !data) return '';
  const entry = data.find((e) => e.slug === slug);
  return entry ? pickBriefing(entry, lang) : '';
}

export function useCatalogLabelMap(kind: CatalogKind): Map<string, string> {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language ?? 'es';
  const { data } = useCatalog(kind);

  return useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;
    for (const entry of data) map.set(entry.slug, pickLabel(entry, lang));
    return map;
  }, [data, lang]);
}
